import React from 'react';
import { Formik, Form, Field } from 'formik';
import { TextField } from 'formik-material-ui';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Box, Button, CircularProgress, Link } from '@material-ui/core';
import { navigate } from 'gatsby';
import { getOr } from 'lodash/fp';

import { useTranslation } from 'react-i18next';
import CustomInputField from '../custom/CustomInputField';
import { RegistrationConfirmationValidation } from './validation';
import { auth, alert } from '../../state';

const VerifyRegistrationForm = ({
  verifyRegistration,
  setAlert,
  user,
  resendRegistrationCode,
  isLoading,
}) => {
  const { t } = useTranslation();
  const resendCode = async () => {
    const email = { email: user.email };
    const response = await resendRegistrationCode(email);
    const successMsg = getOr('', ['payload', 'message'], response);
    const error = getOr('', ['error', 'message'], response);
    if (error) {
      setAlert({ message: error, severity: 'error' });
    }
    setAlert({ message: successMsg, severity: 'success' });
  };

  return (
    <Formik
      enableReinitialize
      validationSchema={RegistrationConfirmationValidation}
      initialValues={{
        email: user.email,
        code: '',
      }}
      onSubmit={async (values, actions) => {
        const response = await verifyRegistration(values);
        const successMsg = getOr('', ['payload', 'message'], response);
        const error = getOr('', ['error', 'message'], response);
        if (error) {
          setAlert({ message: error, severity: 'error' });
          actions.setSubmitting(false);
        }
        if (!error) {
          setAlert({ message: successMsg, severity: 'success' });
          navigate('/dashboard/login');
        }
      }}
    >
      {({ isSubmitting, handleSubmit }) => {
        return (
          <Form>
            <Box pb={0.5}>
              <CustomInputField
                customInput={Field}
                component={TextField}
                trim
                fullWidth
                required
                label={t('securityCode')}
                name="code"
                variant="outlined"
                autoComplete="off"
              />
            </Box>
            <Box align="right" pb={2}>
              {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
              <Link
                component="button"
                type="button"
                variant="caption"
                color="textSecondary"
                onClick={resendCode}
              >
                {isLoading && !isSubmitting ? (
                  <Box pr={4}>
                    <CircularProgress color="primary" size={14} />
                  </Box>
                ) : (
                  t('resendCode')
                )}
              </Link>
            </Box>
            <Box>
              <Button
                fullWidth
                type="submit"
                variant="contained"
                color="primary"
                disabled={isSubmitting}
                onClick={handleSubmit}
                size="large"
              >
                {isSubmitting ? (
                  <CircularProgress size={24} />
                ) : (
                  t('confirmEmail')
                )}
              </Button>
            </Box>
          </Form>
        );
      }}
    </Formik>
  );
};

VerifyRegistrationForm.propTypes = {
  verifyRegistration: PropTypes.func.isRequired,
  setAlert: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  resendRegistrationCode: PropTypes.func.isRequired,
  user: PropTypes.shape({
    email: PropTypes.string,
  }).isRequired,
};

const mapStateToProps = (state) => ({
  user: auth.selectors.selectUser(state),
  isLoading: auth.selectors.selectIsLoading(state),
});

const mapDispatchToProps = (dispatch) => ({
  verifyRegistration: (payload) => dispatch(auth.actions.verifyEmail(payload)),
  setAlert: (payload) => dispatch(alert.actions.open(payload)),
  resendRegistrationCode: (payload) =>
    dispatch(auth.actions.resendRegistrationCode(payload)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(VerifyRegistrationForm);
